import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, ShoppingBag, Check, Clock, Truck, XCircle, 
  Home, ArrowLeft, MapPin, Phone, Calendar, DollarSign,
  Filter, Search, TrendingUp, CheckCircle2, Ban, Loader,
  Eye, Download, Star, MessageCircle
} from 'lucide-react';
import { ordersAPI, authAPI } from '../services/api';

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const user = authAPI.getCurrentUser();

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const data = await ordersAPI.getAll();
      setOrders(data.results || data || []);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
      alert('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        icon: Clock,
        label: 'En attente',
        color: 'yellow',
        gradient: 'from-yellow-500 to-orange-500',
        bgClass: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
        description: 'Commande en cours de validation'
      },
      processing: {
        icon: Loader,
        label: 'En cours',
        color: 'blue',
        gradient: 'from-blue-500 to-cyan-500',
        bgClass: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
        description: 'Préparation de votre commande'
      },
      shipped: {
        icon: Truck,
        label: 'Expédié',
        color: 'purple',
        gradient: 'from-purple-500 to-pink-500',
        bgClass: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
        description: 'Commande en cours de livraison'
      },
      delivered: {
        icon: CheckCircle2,
        label: 'Livré',
        color: 'green',
        gradient: 'from-green-500 to-emerald-500',
        bgClass: 'bg-green-500/20 text-green-400 border-green-500/50',
        description: 'Commande bien reçue'
      },
      cancelled: {
        icon: Ban,
        label: 'Annulé',
        color: 'red',
        gradient: 'from-red-500 to-pink-500',
        bgClass: 'bg-red-500/20 text-red-400 border-red-500/50',
        description: 'Commande annulée'
      }
    };
    return configs[status] || configs.pending;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/60x60?text=Produit';
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = 'http://localhost:8000';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  // Filtrage des commandes
  const filteredOrders = orders.filter(order => {
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesSearch = order.id.toString().includes(searchTerm) || 
                         order.items?.some(item => 
                           item.product_name?.toLowerCase().includes(searchTerm.toLowerCase())
                         );
    return matchesStatus && matchesSearch;
  });

  // Statistiques
  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending' || o.status === 'processing').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length
  };

  if (loading) {
    return (
      // ✅ CHANGEMENT : Harmonisation du fond
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-500/30 border-t-orange-500 mx-auto mb-6"></div>
            <div className="absolute inset-0 rounded-full h-20 w-20 border-4 border-orange-500/10 blur-xl mx-auto animate-pulse"></div>
          </div>
          <div className="text-xl text-gray-300 font-medium animate-pulse">Chargement de vos commandes...</div>
        </div>
      </div>
    );
  }

  return (
    // ✅ CHANGEMENT : Harmonisation du fond
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* ✅ CHANGEMENT : 1. Bulle orange/rose */}
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        {/* ✅ CHANGEMENT : 2. Bulle mauve/bleu */}
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        {/* ✅ CHANGEMENT : 3. Bulle centrale ajoutée */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="hidden sm:block text-white/20">|</span>
              <span className="hidden sm:flex items-center space-x-2 text-white font-bold">
                <ShoppingBag className="text-orange-400" size={20} />
                <span>Mes Commandes</span>
              </span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <Home size={20} className="relative z-10" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        {/* Header avec stats */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-white mb-2 flex items-center space-x-3">
                <TrendingUp className="text-orange-400" size={36} />
                <span>Historique des Commandes</span>
              </h1>
              <p className="text-gray-400">Suivez et gérez toutes vos commandes en temps réel</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total', value: stats.total, icon: ShoppingBag, color: 'blue', gradient: 'from-blue-500 to-cyan-500' },
              { label: 'En cours', value: stats.pending, icon: Clock, color: 'yellow', gradient: 'from-yellow-500 to-orange-500' },
              { label: 'Livrées', value: stats.delivered, icon: CheckCircle2, color: 'green', gradient: 'from-green-500 to-emerald-500' },
              { label: 'Annulées', value: stats.cancelled, icon: Ban, color: 'red', gradient: 'from-red-500 to-pink-500' }
            ].map((stat, idx) => (
              <div key={idx} className="group relative">
                {/* ✅ CHANGEMENT : Réduction de l'opacité (30 -> 5) */}
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.gradient} rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
                <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105">
                  <div className={`w-10 h-10 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center mb-3 shadow-lg transform group-hover:rotate-12 transition-transform`}>
                    <stat.icon className="text-white" size={20} />
                  </div>
                  <div className="text-2xl font-black text-white mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters & Search */}
          <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/20 shadow-xl">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher par numéro ou produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:bg-white/10 transition-all"
                />
              </div>

              {/* Filter buttons */}
              <div className="flex items-center space-x-2 overflow-x-auto pb-2 sm:pb-0">
                {[
                  { value: 'all', label: 'Toutes', icon: Filter },
                  { value: 'pending', label: 'En attente', icon: Clock },
                  { value: 'processing', label: 'En cours', icon: Loader },
                  { value: 'shipped', label: 'Expédié', icon: Truck },
                  { value: 'delivered', label: 'Livré', icon: CheckCircle2 },
                  { value: 'cancelled', label: 'Annulé', icon: Ban }
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setFilterStatus(filter.value)}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all transform hover:scale-105 ${
                      filterStatus === filter.value
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                    }`}
                  >
                    <filter.icon size={16} />
                    <span className="hidden sm:inline">{filter.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Liste des commandes */}
        {filteredOrders.length === 0 ? (
          <div className="relative group">
            {/* ✅ CHANGEMENT : Réduction de l'opacité (30 -> 5) */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-3xl blur opacity-20 group-hover:opacity-5 transition duration-500"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-16 text-center border border-white/20">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
                <div className="relative w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-xl border border-white/20">
                  <ShoppingBag size={48} className="text-orange-400" />
                </div>
              </div>
              <h2 className="text-3xl font-black text-white mb-3">
                {searchTerm || filterStatus !== 'all' ? 'Aucun résultat' : 'Aucune commande'}
              </h2>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Aucune commande ne correspond à vos critères de recherche'
                  : 'Vous n\'avez pas encore passé de commande. Découvrez nos produits !'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <button 
                  onClick={() => navigate('/')} 
                  className="group/btn relative overflow-hidden inline-flex items-center space-x-2"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-pink-600 opacity-0 group-hover/btn:opacity-100 transition-opacity rounded-2xl"></div>
                  <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl transform group-hover/btn:scale-105 transition-all flex items-center space-x-2">
                    <ShoppingBag size={20} />
                    <span>Commencer les achats</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredOrders.map((order, idx) => {
              const statusConfig = getStatusConfig(order.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div 
                  key={order.id}
                  className="group relative"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  {/* ✅ CHANGEMENT : Réduction de l'opacité (30 -> 5) */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${statusConfig.gradient} rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
                  <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 hover:bg-white/15 transition-all transform hover:scale-[1.01]">
                    {/* Header commande avec status */}
                    <div className={`relative overflow-hidden bg-gradient-to-r ${statusConfig.gradient} bg-opacity-20 p-6 border-b border-white/10`}>
                      <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent"></div>
                      <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex items-start space-x-4">
                          <div className="relative">
                            <div className={`absolute inset-0 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl blur opacity-75 animate-pulse`}></div>
                            <div className={`relative w-14 h-14 bg-gradient-to-br ${statusConfig.gradient} rounded-2xl flex items-center justify-center shadow-xl`}>
                              <StatusIcon className="text-white" size={28} />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-2xl font-black text-white">
                                Commande #{order.id}
                              </h3>
                              <span className={`px-4 py-2 rounded-xl text-sm font-bold border-2 backdrop-blur-xl shadow-lg ${statusConfig.bgClass} flex items-center space-x-2`}>
                                <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>
                                <span>{statusConfig.label}</span>
                              </span>
                            </div>
                            <p className="text-sm text-gray-300 mb-2">{statusConfig.description}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-400">
                              <div className="flex items-center space-x-1">
                                <Calendar size={16} />
                                <span>{new Date(order.created_at).toLocaleDateString('fr-FR', { 
                                  day: 'numeric', 
                                  month: 'long', 
                                  year: 'numeric' 
                                })}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Package size={16} />
                                <span>{order.items?.length || 0} article(s)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-400 mb-1 font-medium">Montant Total</div>
                          <div className="text-4xl font-black text-white group-hover:text-orange-400 transition-colors">
                            {parseFloat(order.total_amount).toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">FCFA</div>
                        </div>
                      </div>
                    </div>

                    {/* Détails commande */}
                    <div className="p-6">
                      {/* Informations de livraison */}
                      <div className="mb-6 grid md:grid-cols-2 gap-4">
                        <div className="group/info relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-blue-500/10 opacity-0 group-hover/info:opacity-100 transition-opacity rounded-xl"></div>
                          <div className="relative bg-white/5 rounded-xl p-4 border border-white/10 hover:border-blue-500/30 transition-all">
                            <div className="flex items-start space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                <MapPin className="text-blue-400" size={20} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Adresse de livraison</div>
                                <div className="text-white font-medium">{order.delivery_address}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {order.phone && (
                          <div className="group/info relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 to-green-500/10 opacity-0 group-hover/info:opacity-100 transition-opacity rounded-xl"></div>
                            <div className="relative bg-white/5 rounded-xl p-4 border border-white/10 hover:border-green-500/30 transition-all">
                              <div className="flex items-start space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                  <Phone className="text-green-400" size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Téléphone</div>
                                  <div className="text-white font-medium">{order.phone}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Articles */}
                      <div>
                        <h4 className="text-sm font-black text-gray-400 mb-4 uppercase tracking-wider flex items-center space-x-2">
                          <Package size={16} className="text-orange-400" />
                          <span>Articles commandés</span>
                        </h4>
                        <div className="space-y-3">
                          {order.items?.map((item, itemIdx) => (
                            <div 
                              key={item.id}
                              className="group/item relative overflow-hidden"
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/5 to-orange-500/0 opacity-0 group-hover/item:opacity-100 transition-opacity rounded-xl"></div>
                              <div className="relative flex items-center space-x-4 bg-white/5 rounded-xl p-4 border border-white/10 group-hover/item:border-orange-500/30 transition-all">
                                <div className="relative">
                                  <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover/item:opacity-50 transition"></div>
                                  <img 
                                    src={getImageUrl(item.product_image)}
                                    alt={item.product_name}
                                    className="relative w-20 h-20 rounded-2xl object-cover border-2 border-white/20 shadow-lg transform group-hover/item:scale-110 transition-transform"
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/80x80?text=Produit';
                                    }}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold text-white truncate text-lg group-hover/item:text-orange-400 transition-colors">
                                    {item.product_name}
                                  </div>
                                  <div className="text-sm text-gray-400 flex items-center space-x-2 mt-1">
                                    <span className="font-medium">Quantité: {item.quantity}</span>
                                    <span className="text-gray-600">×</span>
                                    <span>{parseFloat(item.price).toLocaleString()} FCFA</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-black text-white group-hover/item:text-orange-400 transition-colors">
                                    {parseFloat(item.subtotal).toLocaleString()}
                                  </div>
                                  <div className="text-xs text-gray-500">FCFA</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      {order.status === 'delivered' && (
                        <div className="mt-6 flex flex-wrap gap-3">
                          <button className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-400 rounded-xl hover:from-yellow-500/30 hover:to-orange-500/30 transition-all border border-yellow-500/30 hover:border-yellow-500/50 font-medium">
                            <Star size={18} />
                            <span>Laisser un avis</span>
                          </button>
                          <button className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 rounded-xl hover:from-blue-500/30 hover:to-cyan-500/30 transition-all border border-blue-500/30 hover:border-blue-500/50 font-medium">
                            <MessageCircle size={18} />
                            <span>Contacter le vendeur</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}